import React, { useEffect, useRef, useState } from 'react'
import { DateTime } from 'luxon'

import DayCell from './day-cell'
import EventPill from './event-pill'
import { CalendarEvent, EventSegment, WeekRow } from './types'
import {
  getTrackCapacityForRowHeight,
  getVisibleWeeks,
  layoutMultiDayEvents,
  TRACK_CAPACITY,
  TRACK_HEIGHT
} from './helpers'

interface MonthGridProps {
  currentMonth: DateTime
  events: CalendarEvent[]
  onEventClick: (rowId: string) => void
  fitToHeight?: boolean
}

const WEEKDAY_HEADERS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

const MonthGrid: React.FC<MonthGridProps> = ({ currentMonth, events, onEventClick, fitToHeight }) => {
  const bodyRef = useRef<HTMLDivElement>(null)
  const [bodyHeight, setBodyHeight] = useState<number>()

  // Fit to height: the week rows share whatever height the insert leaves, so measure it to know
  // how many event tracks each row can show before the rest collapse into "N more…".
  useEffect(() => {
    const body = bodyRef.current
    if (!fitToHeight || !body) return
    const observer = new ResizeObserver((entries) => setBodyHeight(entries[0].contentRect.height))
    observer.observe(body)
    return () => observer.disconnect()
  }, [fitToHeight])

  // React Compiler handles memoisation — call helpers fresh each render.
  const weeks = getVisibleWeeks(currentMonth)
  const trackCapacity =
    fitToHeight && bodyHeight !== undefined ? getTrackCapacityForRowHeight(bodyHeight / weeks.length) : TRACK_CAPACITY
  layoutMultiDayEvents(events, weeks, trackCapacity)

  const today = DateTime.now().startOf('day')

  return (
    <div className={`flex-1 flex flex-col ${fitToHeight ? 'min-h-0 overflow-hidden' : 'overflow-auto'}`}>
      {/* Weekday header row */}
      <div className="grid grid-cols-7 border-b border-divider bg-surface sticky top-0 z-10">
        {WEEKDAY_HEADERS.map((wd) => (
          <div key={wd} className="px-3 py-2 text-xs font-medium text-muted">
            {wd}
          </div>
        ))}
      </div>

      {/* Grid body */}
      <div
        ref={bodyRef}
        data-testid="calendar-month-body"
        className={`flex flex-col ${fitToHeight ? 'flex-1 min-h-0' : ''}`}
      >
        {weeks.map((week) => (
          <WeekRowView
            key={week.weekStart.toISODate()}
            week={week}
            currentMonth={currentMonth}
            today={today}
            onEventClick={onEventClick}
            trackCapacity={trackCapacity}
            fitToHeight={fitToHeight}
          />
        ))}
      </div>
    </div>
  )
}

interface WeekRowProps {
  week: WeekRow
  currentMonth: DateTime
  today: DateTime
  onEventClick: (rowId: string) => void
  trackCapacity: number
  fitToHeight?: boolean
}

const WeekRowView: React.FC<WeekRowProps> = ({
  week,
  currentMonth,
  today,
  onEventClick,
  trackCapacity,
  fitToHeight
}) => {
  const monthNumber = currentMonth.month

  // Group segments by track so we can render them as a single per-week overlay
  // on top of the day-cell backdrops — this lets bars span multiple columns as
  // a single continuous element rather than one pill per day.
  const segmentsByTrack: Map<number, EventSegment[]> = new Map()
  for (const s of week.segments) {
    const existing = segmentsByTrack.get(s.track)
    if (existing) {
      existing.push(s)
    } else {
      segmentsByTrack.set(s.track, [s])
    }
  }
  const tracks = Array.from(segmentsByTrack.keys()).sort((a, b) => a - b)

  return (
    <div
      data-testid="calendar-week-row"
      className={`relative grid grid-cols-7 border-l border-divider ${
        fitToHeight ? 'flex-1 min-h-0 overflow-hidden' : 'min-h-[140px]'
      }`}
    >
      {/* Day cell backdrops — reserve vertical space for tracks via a spacer child */}
      {week.days.map((day, dayIndex) => (
        <DayCell
          key={day.toISODate()}
          day={day}
          isInCurrentMonth={day.month === monthNumber}
          isToday={day.hasSame(today, 'day')}
          overflowCount={week.overflowByDay[dayIndex]}
          fitToHeight={fitToHeight}
        >
          {/* Spacer — the real event pills are rendered in the overlay below. */}
          <div style={{ height: `${trackCapacity * TRACK_HEIGHT}px` }} aria-hidden />
        </DayCell>
      ))}

      {/* Event overlay: grid on top of day cells */}
      <div className="pointer-events-none absolute inset-0 grid grid-cols-7 px-1 pt-8">
        {tracks.map((track) => {
          const segments = segmentsByTrack.get(track)
          if (!segments) return null
          return segments.map((segment) => {
            const colSpan = segment.endDay - segment.startDay + 1
            return (
              <div
                key={`${segment.eventId}-${track}`}
                className="pointer-events-auto pb-0.5 px-0.5"
                style={{
                  gridColumnStart: segment.startDay + 1,
                  gridColumnEnd: `span ${colSpan}`,
                  gridRowStart: track + 1
                }}
              >
                <EventPill segment={segment} onClick={onEventClick} />
              </div>
            )
          })
        })}
      </div>
    </div>
  )
}

export default MonthGrid
